import React from 'react';
import { Helmet } from 'react-helmet';

const CalculatorSEO = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "StockKhata Financial Calculators - Free Investment & Tax Tools",
    "description": "Free online financial calculators for SIP, EMI, PPF, FD, retirement planning, tax calculations, and investment planning. Professional-grade tools for Indian investors, financial advisors, and taxpayers.",
    "url": "https://stockkhata.com/calculators",
    "applicationCategory": "FinanceApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "INR"
    },
    "featureList": [
      "SIP Calculator",
      "Lumpsum Calculator", 
      "EMI Calculator",
      "PPF Calculator",
      "FD Calculator",
      "Retirement Calculator",
      "NPS Calculator",
      "LTCG Calculator",
      "STCG Calculator",
      "SWP Calculator",
      "RD Calculator",
      "ELSS Calculator",
      "Gratuity Calculator",
      "HRA Calculator",
      "Capital Gains Calculator",
      "CTC Calculator"
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "1250"
    },
    "author": {
      "@type": "Organization",
      "name": "StockKhata",
      "url": "https://stockkhata.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "StockKhata",
      "url": "https://stockkhata.com"
    }
  };

  const calculatorStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Financial Calculators Collection",
    "description": "Comprehensive collection of professional financial calculators for investment planning, tax calculations, and financial decision making",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "item": {
          "@type": "SoftwareApplication",
          "name": "SIP Calculator",
          "description": "Calculate returns on systematic investment plans with compound interest. Perfect for mutual fund SIP planning and wealth creation.",
          "applicationCategory": "Investment Calculator"
        }
      },
      {
        "@type": "ListItem", 
        "position": 2,
        "item": {
          "@type": "SoftwareApplication",
          "name": "EMI Calculator",
          "description": "Calculate loan EMI and repayment schedules for home loans, personal loans, and vehicle financing.",
          "applicationCategory": "Loan Calculator"
        }
      },
      {
        "@type": "ListItem",
        "position": 3, 
        "item": {
          "@type": "SoftwareApplication",
          "name": "PPF Calculator",
          "description": "Calculate Public Provident Fund returns and maturity amount with tax benefits under Section 80C.",
          "applicationCategory": "Tax Saving Calculator"
        }
      },
      {
        "@type": "ListItem",
        "position": 4,
        "item": {
          "@type": "SoftwareApplication", 
          "name": "FD Calculator",
          "description": "Calculate fixed deposit returns and maturity amounts with TDS calculations and interest compounding.",
          "applicationCategory": "Investment Calculator"
        }
      },
      {
        "@type": "ListItem",
        "position": 5,
        "item": {
          "@type": "SoftwareApplication",
          "name": "Retirement Calculator", 
          "description": "Plan your retirement corpus and monthly requirements with inflation-adjusted calculations.",
          "applicationCategory": "Planning Calculator"
        }
      }
    ]
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How to calculate SIP returns?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Use our SIP calculator by entering monthly investment amount, expected annual return, and investment tenure. The calculator uses compound interest formula to show maturity value and total returns."
        }
      },
      {
        "@type": "Question",
        "name": "What is the formula for EMI calculation?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "EMI = [P × r × (1+r)^n] / [(1+r)^n - 1] where P is loan amount, r is monthly interest rate, and n is total number of months."
        }
      },
      {
        "@type": "Question",
        "name": "How to calculate PPF maturity amount?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "PPF calculator considers annual contributions, current interest rate (7.1%), and 15-year lock-in period. Each yearly investment compounds for remaining years."
        }
      }
    ]
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>Free Financial Calculators - SIP, EMI, PPF, FD, Tax & Investment Tools | StockKhata</title>
      <meta name="title" content="Free Financial Calculators - SIP, EMI, PPF, FD, Tax & Investment Tools | StockKhata" />
      <meta name="description" content="Free online financial calculators for SIP, EMI, PPF, FD, retirement planning, tax calculations, and investment planning. Professional-grade tools for Indian investors, financial advisors, and taxpayers. No registration required." />
      <meta name="keywords" content="financial calculator, sip calculator, emi calculator, ppf calculator, fd calculator, retirement calculator, investment calculator, loan calculator, tax calculator, mutual fund calculator, stock calculator, india, free calculator, wealth planning, tax planning, investment planning, financial planning tools, compound interest calculator, retirement planning calculator, tax saving calculator, mutual fund sip calculator, home loan emi calculator, ppf interest calculator, fixed deposit calculator, nps calculator, elss calculator, gratuity calculator, hra calculator, capital gains calculator, ctc calculator, salary calculator, step up sip calculator, swp calculator, recurring deposit calculator" />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="language" content="English" />
      <meta name="author" content="StockKhata | Invest Anywhere, Maintain Only Here" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#1f2937" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://stockkhata.com/calculators" />
      <meta property="og:title" content="Free Financial Calculators - Professional Investment & Tax Tools | StockKhata" />
      <meta property="og:description" content="Access 16+ professional financial calculators for SIP, EMI, PPF, FD, retirement planning, tax calculations, and investment planning. Free tools for Indian investors and financial advisors." />
      <meta property="og:image" content="https://stockkhata.com/og-calculators.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="StockKhata" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content="https://stockkhata.com/calculators" />
      <meta property="twitter:title" content="Free Financial Calculators - Investment & Tax Tools | StockKhata" />
      <meta property="twitter:description" content="Professional financial calculators for SIP, EMI, PPF, FD, retirement planning, and tax calculations. Free tools for Indian investors." />
      <meta property="twitter:image" content="https://stockkhata.com/og-calculators.jpg" />

      {/* Additional SEO Meta Tags */}
      <meta name="application-name" content="StockKhata Financial Calculators" />
      <meta name="apple-mobile-web-app-title" content="StockKhata Calculators" />
      <meta name="msapplication-TileColor" content="#1f2937" />
      <meta name="msapplication-config" content="/browserconfig.xml" />
      
      {/* Canonical URL */}
      <link rel="canonical" href="https://stockkhata.com/calculators" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(calculatorStructuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqStructuredData)}
      </script>

      {/* Additional Meta Tags for Financial Calculators */}
      <meta name="finance:calculator_type" content="investment,loan,tax,retirement,salary,planning" />
      <meta name="finance:target_audience" content="investors,borrowers,retirees,financial_planners,financial_advisors,tax_consultants,individuals,professionals" />
      <meta name="finance:geographic_scope" content="India" />
      <meta name="finance:currency" content="INR" />
      <meta name="finance:investment_products" content="mutual_funds,sip,fd,ppf,nps,elss,stocks,equity,debt" />
      
      {/* Page-specific meta tags */}
      <meta name="page:type" content="calculator" />
      <meta name="page:category" content="finance" />
      <meta name="page:subcategory" content="calculators" />
      <meta name="page:audience" content="investors,borrowers,financial_planners" />
      
      {/* Enhanced SEO Meta Tags */}
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      <meta name="referrer" content="no-referrer-when-downgrade" />
      
      {/* Social Media Meta Tags */}
      <meta name="twitter:site" content="@stockkhata" />
      <meta name="twitter:creator" content="@stockkhata" />
      
      {/* Mobile and App Meta Tags */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      
      {/* Search Engine Optimization */}
      <meta name="google-site-verification" content="your-verification-code" />
      <meta name="msvalidate.01" content="your-verification-code" />
      <meta name="yandex-verification" content="your-verification-code" />
      
      {/* Content Language and Region */}
      <meta name="geo.region" content="IN" />
      <meta name="geo.placename" content="India" />
      <meta name="geo.position" content="20.5937;78.9629" />
      <meta name="ICBM" content="20.5937, 78.9629" />
      
      {/* Financial Calculator Specific Keywords */}
      <meta name="keywords" content="sip calculator india, mutual fund calculator, emi calculator home loan, ppf calculator 2024, fd calculator with tds, retirement planning calculator india, nps calculator tax benefits, elss calculator section 80c, gratuity calculator payment act, hra calculator tax exemption, capital gains calculator india, ctc to take home salary calculator india, step up sip calculator, swp calculator systematic withdrawal, recurring deposit calculator, lumpsum investment calculator, compound interest calculator, tax saving investment calculator 80c, investment planning calculator for beginners, financial planning calculator india, wealth creation calculator mutual funds, mutual fund sip calculator with tax benefits, home loan emi calculator with prepayment, ppf calculator 15 year lock in period, fixed deposit calculator with tds deduction, retirement corpus calculator with monthly expenses, tax deduction calculator under new regime, salary structure calculator with deductions, investment return calculator with inflation, financial goal calculator india, inflation adjusted investment calculator, tax planning calculator for salaried employees" />
      
      {/* Long-tail Keywords for Better SEO */}
      <meta name="long-tail-keywords" content="how to calculate sip returns in mutual funds, emi calculator for home loan in india, ppf calculator with compound interest, fd calculator with quarterly compounding, retirement planning calculator with inflation, nps calculator for government employees, elss tax saving mutual fund calculator, gratuity calculation formula india, hra exemption calculation section 10, capital gains tax calculator for equity, ctc to in hand salary calculator india, step up sip calculator with annual increase, systematic withdrawal plan calculator mutual funds, recurring deposit maturity calculator, lumpsum investment return calculator, compound interest formula calculator, tax saving investment calculator 80c, investment planning calculator for beginners, financial planning calculator india, wealth creation calculator mutual funds, mutual fund sip calculator with tax benefits, home loan emi calculator with prepayment, ppf calculator 15 year lock in period, fixed deposit calculator with tds deduction, retirement corpus calculator with monthly expenses, tax deduction calculator under new regime, salary structure calculator with deductions, investment return calculator with inflation, financial goal calculator india, inflation adjusted investment calculator, tax planning calculator for salaried employees" />
    </Helmet>
  );
};

export default CalculatorSEO; 